<script lang="ts" setup>
import { computed, ref } from 'vue';
import { useQuery } from '@vue/apollo-composable';
import { CodeDocument, CodeType, CouponType } from '@/gql/graphql';
import {
  BAlert,
  BBadge,
  BButton,
  BButtonGroup,
  BModal,
  BSpinner,
} from 'bootstrap-vue-next';
import { ORGANIZATION_USER_ROLES } from '@/constants';
import { formatDate, deepParseDates } from '@/utils/date-utils';
import { highlightJson } from '@/utils/prism-utils';
import { capitalizeFirstLetter } from '@/utils/string-utils';
import CouponUpdate from '@/components/code/CouponUpdate.vue';
import CodeUpdate from '@/components/code/CodeUpdate.vue';
import KitUpdate from '@/components/code/KitUpdate.vue';
import { openRouterLink } from '@/utils/router-utils';

// --- Component properties ---
const props = defineProps({
  code: { type: String, required: true },
});

// --- Component state ---
const selectedCoupon = ref<CouponType | null>(null);
const showModalCodeUpdate = ref(false);
const showModalCouponDetail = ref(false);
const showModalCouponUpdate = ref(false);
const showModalKitDetail = ref(false);
const showModalKitUpdate = ref(false);

// --- Code Object ---
const code = computed<CodeType | null>(() => {
  const codeRes = codeQueryResult.value?.code;
  if (!codeRes) {
    return null;
  }
  return deepParseDates(codeRes) as CodeType;
});

//--- GRAPHQL CODE QUERY ---
const {
  result: codeQueryResult,
  loading: codeQueryInProgress,
  error: codeQueryError,
  refetch: refetchCode,
} = useQuery(CodeDocument, {
  codeString: props.code,
  adminAccess: true,
});

function requestRefetchCode() {
  refetchCode()
    ?.catch((err) => {
      console.error('Refetch code failed:', err);
    })
    ?.then(() => {
      console.debug('Refetched code');
    });
}

function getOrganizationDetails(code: CodeType) {
  // check organization association
  if (!code.organization) {
    return 'not assigned';
  }
  // identify organization owner
  const owner = code.organization.usersAssociated?.find(
    (assoc) =>
      assoc &&
      assoc.userRole === ORGANIZATION_USER_ROLES.OWNER &&
      assoc.removedAt === null
  );
  // return org name + owner email
  return code.organization.name + (owner ? ` (${owner.user?.email})` : '');
}

function codeUpdateRequested() {
  console.debug('Code update requested');
  showModalCodeUpdate.value = true;
}

function couponDetailsRequested(coupon: CouponType) {
  console.debug('Coupon details requested:', coupon.couponType);
  selectedCoupon.value = coupon;
  showModalCouponDetail.value = true;
}

function couponUpdateRequested(coupon: CouponType) {
  console.debug('Coupon update requested:', coupon.couponType);
  selectedCoupon.value = coupon;
  showModalCouponUpdate.value = true;
}

function kitDetailsRequested() {
  console.debug('Kit details requested');
  if (!code.value?.kit) {
    console.error('Kit details requested, but kit is not available');
    return;
  }
  showModalKitDetail.value = true;
}

function kitUpdateRequested() {
  console.debug('Kit update requested');
  if (!code.value?.kit) {
    console.error('Kit update requested, but kit is not available');
    return;
  }
  showModalKitUpdate.value = true;
}

function closeAndRefresh() {
  closeModals();
  requestRefetchCode();
}

function closeModals() {
  selectedCoupon.value = null;
  showModalCodeUpdate.value = false;
  showModalCouponDetail.value = false;
  showModalCouponUpdate.value = false;
  showModalKitDetail.value = false;
  showModalKitUpdate.value = false;
}
</script>
<template>
  <div>
    <!-- Link back to project list -->
    <b-button
      @click="openRouterLink({ name: 'AdminCodesList' }, $router, $event)"
      variant="outline-dark"
      class="mb-3"
      size="sm"
    >
      <font-awesome-icon icon="angle-left" class="me-2" />
      Back to All Codes
    </b-button>

    <!-- Code title -->
    <h1>
      <font-awesome-icon :icon="['fas', 'barcode']" class="me-2" />
      {{ props.code }}
    </h1>
    <hr />

    <!-- Error Message -->
    <b-alert v-model="codeQueryError" variant="danger" class="mt-3">
      {{ codeQueryError?.message }}
      <b-button
        @click="requestRefetchCode"
        variant="outline-dark"
        class="btn-sm"
      >
        <font-awesome-icon :icon="['fas', 'sync']" />
        Retry</b-button
      >
    </b-alert>

    <!-- Busy Loading -->
    <div v-if="codeQueryInProgress">
      <b-spinner label="Loading code details..." />
    </div>

    <!-- Code detail -->
    <div v-if="code">
      <!-- Code metadata -->
      <div class="mb-3">
        <p class="mb-1"><strong>Code: </strong> {{ code.code }}</p>
        <p class="mb-1">
          <strong>Order: </strong>
          <span v-if="code.orderRef"> {{ code.orderRef }}</span>
          <span v-else> not specified</span>
        </p>
        <p class="mb-1">
          <strong>Organization: </strong>
          {{ getOrganizationDetails(code) }}
        </p>
        <p class="mb-1">
          <strong>Comment: </strong>
          <span v-if="code.comment">
            {{ code.comment }}
          </span>
          <span v-else> not specified</span>
        </p>
        <BButton
          variant="outline-secondary"
          size="sm"
          @click="codeUpdateRequested"
        >
          <font-awesome-icon icon="pencil" class="me-1" />
          Edit
        </BButton>
      </div>

      <!-- Kit details -->
      <div class="mb-1" v-if="code.kit">
        <h3>
          <b-badge variant="primary" class="me-2">
            <font-awesome-icon :icon="['fas', 'cube']" class="me-2" />
            Kit
          </b-badge>
        </h3>
        <p class="mb-1"><strong>Name: </strong> {{ code.kit.name }}</p>
        <p class="mb-1"><strong>Barcode: </strong> {{ code.kit.barcode }}</p>
        <p class="mb-1">
          <strong>UMI length: </strong> {{ code.kit.umiLength }}
        </p>
        <p class="mb-1">
          <strong>Samples: </strong> {{ code.kit.sampleCount }}
        </p>
        <p class="mb-1">
          <strong>Manufactured: </strong>
          {{ formatDate(code.kit.manufacturedAt) }}
        </p>
        <p class="mb-1">
          <strong>Expiration: </strong> {{ code.kit.expirationMonths }} months
          from manufacture date
        </p>
        <p class="mb-1">
          <strong>Description: </strong> {{ code.kit.description }}
        </p>
        <p class="mb-1">
          <strong>Version: </strong>
          {{ code.kit.version ? code.kit.version : 'not specified' }}
        </p>
        <BButtonGroup>
          <BButton
            variant="outline-secondary"
            size="sm"
            @click="kitUpdateRequested"
          >
            <font-awesome-icon icon="pencil" class="me-1" />
            Edit
          </BButton>
          <BButton
            variant="outline-secondary"
            size="sm"
            @click="kitDetailsRequested"
          >
            <font-awesome-icon icon="eye" class="me-1" />
            View details
          </BButton>
        </BButtonGroup>
      </div>

      <!-- License details -->
      <div class="mb-1" v-if="code.license">
        <strong>License: </strong>
        <span> {{ code.license.licenseType }}</span>
      </div>

      <!-- List Coupons -->
      <template v-for="(coupon, cidx) in code.coupons" :key="cidx">
        <div v-if="coupon" class="mt-3">
          <h3 class="mt-3 mb-1">
            <b-badge
              :variant="
                coupon.couponType?.includes('compute') ? 'warning' : 'success'
              "
              class="me-2"
            >
              <font-awesome-icon :icon="['fas', 'ticket']" class="me-2" />
              {{ capitalizeFirstLetter(coupon.couponType) }}
              coupon
            </b-badge>
          </h3>
          <p>
            <strong>Status: </strong>
            {{ coupon.unitsRemaining }} of {{ coupon.unitsTotal }}
            {{ coupon.units }} remaining<br />
            <strong>Created: </strong> {{ formatDate(coupon.createdAt) }}<br />
            <strong>Expires: </strong>
            {{ formatDate(coupon.expiresAt, 'no expiration') }}
          </p>
          <BButtonGroup>
            <BButton
              variant="outline-secondary"
              size="sm"
              @click="couponUpdateRequested(coupon)"
            >
              <font-awesome-icon icon="pencil" class="me-1" />
              Edit
            </BButton>
            <BButton
              variant="outline-secondary"
              size="sm"
              @click="couponDetailsRequested(coupon)"
            >
              <font-awesome-icon icon="eye" class="me-1" />
              View details
            </BButton>
          </BButtonGroup>
        </div>
      </template>

      <!-- Kit details modal -->
      <div v-if="code.kit && showModalKitDetail">
        <BModal v-model="showModalKitDetail" @hide="closeModals" size="lg">
          <template #title>
            <font-awesome-icon :icon="['fas', 'cube']" class="me-2" />
            {{ code.kit.name }} kit details
          </template>
          <p class="mb-1"><strong>Barcode: </strong> {{ code.kit.barcode }}</p>
          <p class="mb-1">
            <strong>UMI length: </strong> {{ code.kit.umiLength }}
          </p>
          <p class="mb-1">
            <strong>Samples: </strong> {{ code.kit.sampleCount }}
          </p>
          <p class="mb-1">
            <strong>Manufactured: </strong>
            {{ formatDate(code.kit.manufacturedAt) }}
          </p>
          <p class="mb-1">
            <strong>Expiration: </strong> {{ code.kit.expirationMonths }} months
            from manufacture date
          </p>
          <p class="mb-1">
            <strong>Description: </strong> {{ code.kit.description }}
          </p>
          <p class="mb-1">
            <strong>Version: </strong>
            {{ code.kit.version ? code.kit.version : 'not specified' }}
          </p>
          <pre
            class="code-highlight"
            v-html="highlightJson(code.kit.details)"
          ></pre>
          <template #footer>
            <BButton @click="closeModals" variant="secondary"> Close </BButton>
          </template>
        </BModal>
      </div>

      <!-- Coupon details modal -->
      <div v-if="selectedCoupon">
        <BModal v-model="showModalCouponDetail" @hide="closeModals" size="lg">
          <template #title>
            <font-awesome-icon icon="ticket" class="me-2" />
            {{ capitalizeFirstLetter(selectedCoupon.couponType) }} coupon
            details
          </template>
          <p>
            <strong>Status: </strong>
            {{ selectedCoupon.unitsRemaining }} of
            {{ selectedCoupon.unitsTotal }}
            {{ selectedCoupon.units }} remaining<br />
            <strong>Created: </strong> {{ formatDate(selectedCoupon.createdAt)
            }}<br />
            <strong>Expires: </strong>
            {{ formatDate(selectedCoupon.expiresAt, 'no expiration') }}
          </p>
          <pre
            class="code-highlight"
            v-html="highlightJson(selectedCoupon.details)"
          ></pre>
          <template #footer>
            <BButton @click="closeModals" variant="secondary"> Close </BButton>
          </template>
        </BModal>
      </div>

      <!-- Code update modal -->
      <div v-if="showModalCodeUpdate">
        <CodeUpdate
          :code="code"
          :show="showModalCodeUpdate"
          @close="closeModals"
          @close-and-refresh="closeAndRefresh"
        />
      </div>

      <!-- Kit update modal -->
      <div v-if="code.kit && showModalKitUpdate">
        <KitUpdate
          :kit="code.kit"
          :show="showModalKitUpdate"
          @close="closeModals"
          @close-and-refresh="closeAndRefresh"
        />
      </div>

      <!-- Coupon update modal -->
      <div v-if="selectedCoupon && showModalCouponUpdate">
        <CouponUpdate
          :coupon="selectedCoupon"
          :show="showModalCouponUpdate"
          @close="closeModals"
          @close-and-refresh="closeAndRefresh"
        />
      </div>
    </div>
  </div>
</template>
